import { LitElement, html, css } from "lit";

export class RDDeviceSelector extends LitElement {
  static properties = {
    // JSON-driven items configuration (like TopBar)
    items: { type: Array },
    // Size variant: 'small' | 'normal' | 'large'
    size: { type: String },
  };

  static styles = css`
    :host {
      display: inline-block;
      font-family: var(--font-family);
    }

    /* Container for the whole toolbar group */
    .toolbar-group {
      display: flex;
      align-items: center;
      background-color: var(--bg-panel);
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius);
      padding: var(--toolbar-padding, 2px);
      gap: var(--toolbar-gap, 2px);
      transition: border-color var(--theme-transition);
      position: relative;
    }

    /* Size Variants */
    .toolbar-group.small {
      --toolbar-padding: 2px;
      --toolbar-gap: 1px;
      --btn-height: 22px;
      --btn-padding: 4px 6px;
      --icon-size: 14px;
      --font-size: 10px;
      --input-height: 20px;
      --input-width: 32px;
      --separator-height: 12px;
      --base-star-size: 5px;
      --base-star-offset: 1px;
    }

    .toolbar-group.normal {
      --toolbar-padding: 3px;
      --toolbar-gap: 2px;
      --btn-height: 28px;
      --btn-padding: 6px 10px;
      --icon-size: 16px;
      --font-size: 11px;
      --input-height: 26px;
      --input-width: 38px;
      --separator-height: 16px;
      --base-star-size: 6px;
      --base-star-offset: 2px;
    }

    .toolbar-group.large {
      --toolbar-padding: 3px;
      --toolbar-gap: 3px;
      --btn-height: 32px;
      --btn-padding: 7px 12px;
      --icon-size: 18px;
      --font-size: 12px;
      --input-height: 28px;
      --input-width: 44px;
      --separator-height: 18px;
      --base-star-size: 7px;
      --base-star-offset: 2px;
    }

    /* Base Button Style */
    .btn {
      appearance: none;
      background: transparent;
      border: none;
      border-radius: calc(var(--border-radius) - 2px);
      color: var(--text-muted);
      cursor: pointer;
      padding: var(--btn-padding, 5px 8px);
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      transition: all var(--theme-transition);
      outline: none;
      height: var(--btn-height, 26px);
    }

    .btn:hover {
      color: var(--text-main);
      background-color: var(--item-hover);
    }

    .btn.active {
      color: var(--primary-color);
      background-color: var(--selection-bg);
    }

    /* Separator Line */
    .separator {
      width: 1px;
      height: var(--separator-height, 14px);
      background-color: var(--border-color);
      margin: 0 4px;
    }

    /* Base Star Indicator */
    .base-star {
      position: absolute;
      top: var(--base-star-offset, 2px);
      right: var(--base-star-offset, 2px);
      width: var(--base-star-size, 6px) !important;
      height: var(--base-star-size, 6px) !important;
      color: var(--theme-warning, #ffaa02);
      filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1));
      pointer-events: none;
    }

    /* Inputs (Width/Scale) */
    .input-group {
      display: flex;
      align-items: center;
      padding: 0 8px;
      cursor: text;
      height: var(--input-height, 24px);
      border-radius: calc(var(--border-radius) - 2px);
      transition: all 0.2s ease;
    }
    .input-group:hover {
      background-color: var(--item-hover);
    }
    .input-group:focus-within {
      background-color: var(--bg-panel);
      box-shadow: 0 0 0 2px var(--primary-color);
    }

    .raw-input {
      font-family: "JetBrains Mono", monospace;
      font-size: var(--font-size, 11px);
      color: var(--text-muted);
      background: transparent;
      border: none;
      text-align: right;
      outline: none;
      padding: 0;
      margin: 0;
      min-width: 24px;
    }
    .raw-input:focus {
      color: var(--text-main);
    }
    /* Hide number input arrows/spinners */
    .raw-input::-webkit-outer-spin-button,
    .raw-input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    .raw-input[type="number"] {
      -moz-appearance: textfield;
    }
    .unit {
      font-family: "JetBrains Mono", monospace;
      font-size: var(--font-size, 11px);
      color: var(--text-muted);
      margin-left: 2px;
      pointer-events: none;
      user-select: none;
    }

    svg,
    ::slotted([slot^="icon-"]) {
      width: var(--icon-size, 16px);
      height: var(--icon-size, 16px);
      fill: currentColor;
    }

    ::slotted([slot^="icon-"]) svg,
    ::slotted([slot^="icon-"]) i {
      width: var(--icon-size, 16px) !important;
      height: var(--icon-size, 16px) !important;
      font-size: var(--icon-size, 16px) !important;
    }

    .rotate-icon {
      transition: transform 0.3s ease;
    }
  `;

  constructor() {
    super();
    this.items = [];
    this.size = 'normal'; // default size
    this._boundHandleDeviceManagerUpdate = this._handleDeviceManagerUpdate.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    // Sync from DeviceManager
    this._syncFromDeviceManager();

    // Subscribe to DeviceManager updates
    if (window["DeviceManager"]) {
      this._deviceManagerUnsubscribe = window["DeviceManager"].on("updated", this._boundHandleDeviceManagerUpdate);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Unsubscribe from DeviceManager
    if (this._deviceManagerUnsubscribe) {
      this._deviceManagerUnsubscribe();
    }
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    // Inject icons into light DOM when items change
    if (changedProperties.has('items')) {
      this._injectIconsToLightDOM();
    }
  }

  /**
   * Inject icons into light DOM with slot attributes
   * @private
   */
  _injectIconsToLightDOM() {
    // Remove existing icon elements from light DOM
    const existingIcons = this.querySelectorAll('[slot^="icon-"]');
    existingIcons.forEach(el => el.remove());

    // Create icon elements for items that have icons
    this.items.forEach((item) => {
      if (item.icon && item.id) {
        const iconElement = document.createElement('span');
        iconElement.setAttribute('slot', `icon-${item.id}`);
        iconElement.innerHTML = item.icon;
        iconElement.style.cssText = 'display: inline-flex; align-items: center; justify-content: center;';
        this.appendChild(iconElement);
      }
    });
  }

  /**
   * Sync configuration from DeviceManager
   * @private
   */
  _syncFromDeviceManager() {
    const deviceManager = window["DeviceManager"];
    if (!deviceManager) {
      console.warn("[DeviceSelector] DeviceManager not available");
      return;
    }

    // Get items configuration from DeviceManager
    if (typeof deviceManager.getDeviceSelectorConfig === 'function') {
      this.items = deviceManager.getDeviceSelectorConfig();
    }
  }

  /**
   * Handle DeviceManager updates
   * @private
   */
  _handleDeviceManagerUpdate(data) {
    const deviceManager = window["DeviceManager"];
    if (!deviceManager) return;

    // Refresh configuration from DeviceManager
    if (typeof deviceManager.getDeviceSelectorConfig === 'function') {
      this.items = deviceManager.getDeviceSelectorConfig();
    }

    this.requestUpdate();
  }

  // === Render Methods ===

  /**
   * Handle item click
   */
  _handleItemClick(item, event) {
    if (item.disabled) return;
    if (item.onClick) {
      item.onClick(item, event);
      this.requestUpdate();
    }
  }

  /**
   * Render device button
   */
  _renderDeviceButton(item) {
    return html`
      <button
        class="btn ${item.active ? 'active' : ''}"
        @click="${(e) => this._handleItemClick(item, e)}"
        title="${item.tooltip || ''}"
        ?disabled="${item.disabled}"
      >
        <slot name="icon-${item.id}"></slot>
        ${item.showBaseStar ? html`<svg class="base-star" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path></svg>` : ''}
      </button>
    `;
  }

  /**
   * Render input field (width or scale)
   */
  _renderInput(item) {
    return html`
      <div class="input-group" title="${item.tooltip || ''}">
        <input
          class="raw-input"
          style="width: ${item.inputWidth || 'var(--input-width, 38px)'};"
          type="${item.inputType || 'number'}"
          .value="${item.value || ''}"
          @input="${(e) => item.onInput && item.onInput(e, item)}"
          @change="${(e) => item.onChange && item.onChange(e, item)}"
          @keydown="${(e) => item.onKeydown && item.onKeydown(e, item)}"
          @focus="${(e) => e.target.select()}"
        />
        <span class="unit">${item.unit || ''}</span>
      </div>
    `;
  }

  /**
   * Render rotate button
   */
  _renderRotateButton(item) {
    return html`
      <button
        class="btn ${item.active ? 'active' : ''}"
        ?disabled="${item.disabled}"
        style="opacity: ${item.disabled ? 0.3 : 1}"
        @click="${(e) => this._handleItemClick(item, e)}"
        title="${item.tooltip || ''}"
      >
        <svg class="rotate-icon" viewBox="0 0 24 24" style="transform: rotate(${item.rotateAngle || '0deg'})">
          <path d="M16.48 2.52c3.27 1.55 5.61 4.72 5.97 8.48h1.5C23.44 4.84 18.29 0 12 0l-.66.03 3.81 3.81 1.33-1.32zm-6.25-.77c-.59-.59-1.54-.59-2.12 0L1.75 8.11c-.59.59-.59 1.54 0 2.12l12.02 12.02c.59.59 1.54.59 2.12 0l6.36-6.36c.59-.59.59-1.54 0-2.12L10.23 1.75zm4.6 19.44L2.81 9.17l6.36-6.36 12.02 12.02-6.36 6.36zm-7.52-8.02l-1.33 1.32v-5.2H4.13l1.33 1.32L2.52 7.52C.97 10.79.97 14.51 2.52 17.78l1.33-1.32 1.33 1.32c-1.35-2.76-1.35-5.96 0-8.73l2.12 2.12zM20 18.15V24h4v-5.85h-4z" />
        </svg>
      </button>
    `;
  }

  /**
   * Render separator
   */
  _renderSeparator() {
    return html`<div class="separator"></div>`;
  }

  /**
   * Render generic button
   */
  _renderButton(item) {
    return html`
      <button
        class="btn ${item.active ? 'active' : ''}"
        data-id="${item.id}"
        @click="${(e) => this._handleItemClick(item, e)}"
        title="${item.tooltip || ''}"
        ?disabled="${item.disabled}"
      >
        <slot name="icon-${item.id}"></slot>
        ${item.label ? html`<span>${item.label}</span>` : ''}
      </button>
    `;
  }

  /**
   * Render item based on type
   */
  _renderItem(item) {
    switch (item.type) {
      case 'device-button':
        return this._renderDeviceButton(item);
      case 'input':
        return this._renderInput(item);
      case 'rotate-button':
        return this._renderRotateButton(item);
      case 'separator':
        return this._renderSeparator();
      case 'button':
        return this._renderButton(item);
      default:
        return '';
    }
  }

  render() {
    const sizeClass = ['small', 'normal', 'large'].includes(this.size) ? this.size : 'normal';
    return html`
      <div class="toolbar-group ${sizeClass}">
        ${this.items.map((item) => this._renderItem(item))}
      </div>
    `;
  }
}
customElements.define("rd-device-selector", RDDeviceSelector);
